import React from "react";
import LegalLayout, { H2, H3, P, UL, Note } from "../components/composite/LegalLayout";
import usePageMeta from "../hooks/usePageMeta";

const TOC = [
  { id: "agreement", label: "Agreement to these terms" },
  { id: "definitions", label: "Definitions" },
  { id: "service", label: "The Service" },
  { id: "accounts", label: "Accounts and security" },
  { id: "plans", label: "Plans, fees and trials" },
  { id: "your-responsibilities", label: "Your responsibilities" },
  { id: "acceptable-use", label: "Acceptable use" },
  { id: "prohibited-uses", label: "Prohibited decisions and screening" },
  { id: "ai-output", label: "Automated analysis: no reliance" },
  { id: "platforms", label: "Third-party platforms" },
  { id: "data", label: "Your data and our IP" },
  { id: "availability", label: "Availability and changes" },
  { id: "confidentiality", label: "Confidentiality" },
  { id: "term", label: "Term, suspension and termination" },
  { id: "disclaimer", label: "Disclaimer of warranties" },
  { id: "liability", label: "Limitation of liability" },
  { id: "indemnity", label: "Indemnification" },
  { id: "law", label: "Governing law and disputes" },
  { id: "general", label: "General" },
  { id: "contact", label: "Contact" },
];

export default function TermsOfService() {
  usePageMeta({
    title: "Terms of Service · RepCheck",
    description:
      "The terms governing use of RepCheck's reputation monitoring service, including acceptable use, responsibilities when monitoring social media accounts, and limits on reliance on automated analysis.",
  });

  return (
    <LegalLayout
      title="Terms of Service"
      updated="4 September 2026"
      toc={TOC}
      summary="These terms are a binding agreement between you and RepCheck. They explain what the Service does, what you are responsible for when you monitor social media accounts, and the important limits on what our automated analysis means."
    >
      <H2 id="agreement">1. Agreement to these terms</H2>
      <P>
        These Terms of Service (the "Terms") govern your access to and use of the RepCheck website, web
        application, RepGuard browser extension and related services (the "Service"), provided by RepCheck,
        operating from the United States ("RepCheck", "we", "us").
      </P>
      <P>
        By creating an account, accessing or using the Service, you agree to these Terms and to our Privacy
        Policy. If you do not agree, do not use the Service. If you are entering into these Terms on behalf of an
        organisation, you confirm you have authority to bind that organisation, and "you" means that
        organisation.
      </P>
      <P>
        The Service is intended for business use by people aged 18 or over. It is not offered to consumers for
        personal, family or household purposes.
      </P>

      <H2 id="definitions">2. Definitions</H2>
      <UL>
        <li><strong>"Customer Data"</strong> - data you submit to the Service, including client records and the account handles you ask us to monitor.</li>
        <li><strong>"Monitored Account"</strong> - a social media account you instruct the Service to monitor.</li>
        <li><strong>"Collected Content"</strong> - publicly available posts, profile information, and public replies to those posts, retrieved from a platform in connection with a Monitored Account.</li>
        <li><strong>"Output"</strong> - risk scores, risk categories, explanations, reply-level assessments, alerts and reports generated by the Service.</li>
      </UL>

      <H2 id="service">3. The Service</H2>
      <P>
        RepCheck retrieves publicly available social media content associated with the Monitored Accounts you
        specify, analyses that content using automated systems including artificial intelligence, and presents
        Output indicating potential reputational risk. The Service may also run monitoring on a recurring
        schedule you configure and send you alerts.
      </P>
      <P>
        Platform coverage varies and may change. We may add, modify, limit or discontinue support for any
        platform at any time, including where a platform changes its technology, terms or access controls.
      </P>

      <H2 id="accounts">4. Accounts and security</H2>
      <UL>
        <li>You must provide accurate registration information and keep it current.</li>
        <li>You are responsible for all activity under your account and for the confidentiality of your credentials.</li>
        <li>You must notify us promptly at <a href="mailto:security@getrepcheck.com" className="text-brand-600 underline underline-offset-2 hover:text-brand-700">security@getrepcheck.com</a> of any suspected unauthorised access.</li>
        <li>You are responsible for the acts and omissions of users you invite to your workspace.</li>
      </UL>

      <H2 id="plans">5. Plans, fees and trials</H2>
      <UL>
        <li>Paid plans, where offered, are billed in advance on the cycle shown at purchase and are non-refundable except where required by law or expressly stated.</li>
        <li>Fees exclude taxes; you are responsible for applicable taxes other than taxes on our income.</li>
        <li>We may change pricing on notice; changes take effect at your next renewal.</li>
        <li>Pilot, beta or free access may be provided with limited or no functionality guarantees and may be modified or withdrawn at any time.</li>
        <li>Failure to pay may result in suspension or termination.</li>
      </UL>

      <H2 id="your-responsibilities">6. Your responsibilities</H2>
      <P>
        The Service processes information about real people who are not our customers. You accept the following
        responsibilities, which are fundamental to this agreement:
      </P>
      <UL>
        <li><strong>Lawful basis.</strong> You are the controller of the monitoring you configure. You are solely responsible for having a valid lawful basis to monitor each Monitored Account and to process the resulting information, and for meeting any transparency, notice or consent obligations that apply to you.</li>
        <li><strong>Legitimate relationship or interest.</strong> You will only monitor accounts where you have a genuine, lawful business interest - for example accounts belonging to your organisation, to talent or creators you represent or work with, or to public figures relevant to a legitimate brand-safety purpose.</li>
        <li><strong>Proportionality.</strong> You will keep monitoring proportionate to that purpose and will not monitor private individuals in a way that would be unfair, excessive or intrusive.</li>
        <li><strong>Platform terms.</strong> You are responsible for ensuring your use of the Service is consistent with the terms of the platforms concerned, and for any credentials or sessions you supply or authorise for use with the Service.</li>
        <li><strong>Handling Output.</strong> You will treat Output as confidential, review it with a human before acting on it, and not publish it in a way that presents it as a factual finding about any person.</li>
      </UL>

      <H2 id="acceptable-use">7. Acceptable use</H2>
      <P>You must not, and must not permit anyone to:</P>
      <UL>
        <li>use the Service to stalk, harass, intimidate, dox, surveil or retaliate against any person;</li>
        <li>monitor accounts belonging to children, or target people on the basis of race, ethnicity, religion, disability, sexual orientation, gender identity, health status, immigration status, or political or trade-union affiliation;</li>
        <li>use the Service to suppress lawful speech, journalism, whistleblowing, protected industrial activity or protected legal rights;</li>
        <li>attempt to access private accounts, private messages or non-public content, or circumvent access controls;</li>
        <li>reverse engineer, decompile, scrape, resell, sublicense or create a competing service from the Service or its Output, or use it to build a machine-learning model;</li>
        <li>upload malware, interfere with the Service, probe or load-test it without our written consent, or exceed documented rate limits;</li>
        <li>use the Service in violation of any applicable law, including data protection, anti-discrimination, employment, consumer-protection and export laws.</li>
      </UL>
      <P>We may investigate suspected violations and suspend access to protect the Service, our users or third parties.</P>

      <H2 id="prohibited-uses">8. Prohibited decisions and screening</H2>
      <Note>
        <strong>RepCheck is not a consumer reporting agency, and Output is not a consumer report.</strong> You
        must not use the Service or its Output, in whole or in part, as a factor in establishing eligibility for
        employment, promotion, retention or reassignment; credit or insurance; housing or tenancy; education; or
        any other purpose covered by the U.S. Fair Credit Reporting Act or comparable law in any jurisdiction.
      </Note>
      <P>
        You must not use Output as the sole or determinative basis for any decision that produces legal effects
        for a person or similarly significantly affects them. If you intend to use social media analysis for
        screening purposes, you must obtain a compliant service designed for that purpose.
      </P>

      <H2 id="ai-output">9. Automated analysis: no reliance</H2>
      <P>
        Output is generated by automated systems, including large language models, and is <strong>probabilistic
        and opinion-based</strong>. It is not a statement of fact about any person and is not legal, employment,
        compliance or investigative advice.
      </P>
      <UL>
        <li>Output will contain false positives and false negatives, and may misinterpret satire, quotation, reclaimed language, dialect, cultural context, images and non-English content.</li>
        <li>A risk score or category reflects a pattern match, not a determination that a person has done anything wrong.</li>
        <li>We do not warrant that the Service will detect all risky content, or that flagged content is in fact risky.</li>
        <li>You are solely responsible for any action you take, or do not take, based on Output.</li>
      </UL>

      <H2 id="platforms">10. Third-party platforms</H2>
      <P>
        RepCheck is not affiliated with, endorsed by, or sponsored by any social media platform. All platform
        names and marks belong to their owners. The Service depends on third-party platforms that we do not
        control; their availability, technology, rate limits and terms may change without notice, which may
        degrade, interrupt or end our ability to collect content from them. We are not liable for such changes.
      </P>

      <H2 id="data">11. Your data and our intellectual property</H2>
      <H3>11.1 Your data</H3>
      <P>
        As between you and us, you retain all rights in Customer Data. You grant us a worldwide, non-exclusive,
        royalty-free licence to host, process, transmit and display Customer Data and Collected Content solely to
        provide, secure and support the Service, and as otherwise described in our Privacy Policy. Where we
        process personal data on your behalf we do so as your processor; a data processing agreement is available
        on request.
      </P>
      <H3>11.2 Our intellectual property</H3>
      <P>
        We retain all rights in the Service, including its software, models, interfaces, documentation and marks.
        Subject to these Terms, we grant you a limited, non-exclusive, non-transferable, revocable right to
        access and use the Service during your subscription for your internal business purposes.
      </P>
      <H3>11.3 Feedback</H3>
      <P>
        If you send us suggestions, we may use them without restriction or obligation to you.
      </P>
      <H3>11.4 Aggregated data</H3>
      <P>
        We may generate aggregated, de-identified statistics about use of the Service and use them to operate and
        improve it, provided they do not identify you, your clients or any monitored individual.
      </P>

      <H2 id="availability">12. Availability and changes</H2>
      <P>
        We aim to keep the Service available but do not guarantee uninterrupted or error-free operation. Unless a
        separate written service level agreement applies, the Service is provided without any uptime commitment.
        We may modify features, and we will give reasonable notice of materially adverse changes where
        practicable.
      </P>

      <H2 id="confidentiality">13. Confidentiality</H2>
      <P>
        Each party may receive non-public information of the other. The receiving party will use it only to
        perform under these Terms, protect it with at least reasonable care, and not disclose it except to
        personnel and advisers bound by similar obligations, or as required by law with notice where permitted.
      </P>

      <H2 id="term">14. Term, suspension and termination</H2>
      <UL>
        <li>These Terms apply while you use the Service. You may stop and close your account at any time.</li>
        <li>We may suspend or terminate access immediately if you materially breach these Terms, if required by law, or if your use poses a risk to the Service or to third parties.</li>
        <li>On termination, your right to use the Service ends. You may request an export of Customer Data within 30 days, after which we may delete it in line with our retention practices.</li>
        <li>Sections that by their nature should survive - including Sections 8, 9, 11, 13, 15, 16, 17 and 18 - survive termination.</li>
      </UL>

      <H2 id="disclaimer">15. Disclaimer of warranties</H2>
      <P>
        To the fullest extent permitted by law, the Service and all Output are provided <strong>"as is" and "as
        available"</strong>, without warranties of any kind, whether express, implied or statutory, including
        implied warranties of merchantability, fitness for a particular purpose, title, non-infringement, and any
        warranty as to accuracy, completeness, reliability or results. Some jurisdictions do not allow the
        exclusion of certain warranties, so parts of this section may not apply to you.
      </P>

      <H2 id="liability">16. Limitation of liability</H2>
      <P>
        To the fullest extent permitted by law: (a) neither party is liable for indirect, incidental, special,
        consequential, exemplary or punitive damages, or for lost profits, revenue, goodwill, or data, however
        caused; and (b) our total aggregate liability arising out of or relating to these Terms or the Service
        will not exceed the greater of the amounts you paid us for the Service in the twelve months before the
        event giving rise to the claim, or USD 100.
      </P>
      <P>
        Nothing in these Terms excludes or limits liability that cannot lawfully be excluded, including for death
        or personal injury caused by negligence, or for fraud.
      </P>

      <H2 id="indemnity">17. Indemnification</H2>
      <P>
        You will defend, indemnify and hold harmless RepCheck and its officers, employees and agents from and
        against any claims, damages, liabilities, penalties and reasonable costs (including legal fees) arising
        out of or relating to:
      </P>
      <UL>
        <li>your Customer Data and your selection of Monitored Accounts;</li>
        <li>your lack of a lawful basis for monitoring, or failure to meet notice, transparency or consent obligations;</li>
        <li>your use of Output, including any decision taken or communication published on the basis of it;</li>
        <li>your breach of these Terms, of applicable law, or of any third-party platform's terms; and</li>
        <li>claims by any monitored individual arising from monitoring you instructed.</li>
      </UL>

      <H2 id="law">18. Governing law and disputes</H2>
      <P>
        These Terms are governed by the laws of the State of Delaware, United States, without regard to
        conflict-of-laws rules. The parties submit to the exclusive jurisdiction of the state and federal courts
        located in New Castle County, Delaware, except that either party may seek injunctive relief in any court
        of competent jurisdiction to protect its intellectual property or confidential information.
      </P>
      <P>
        Before filing a claim, each party agrees to attempt in good faith to resolve the dispute informally by
        contacting the other and allowing 30 days to resolve it. Nothing in this section deprives a consumer of
        the protection of mandatory law in their country of residence.
      </P>

      <H2 id="general">19. General</H2>
      <UL>
        <li><strong>Entire agreement.</strong> These Terms and the Privacy Policy are the entire agreement between us on this subject and supersede prior discussions.</li>
        <li><strong>Changes.</strong> We may update these Terms; we will update the "Last updated" date and, for material changes, give reasonable advance notice. Continued use after the effective date constitutes acceptance.</li>
        <li><strong>Assignment.</strong> You may not assign these Terms without our consent; we may assign them in connection with a merger, acquisition or sale of assets.</li>
        <li><strong>Severability.</strong> If a provision is unenforceable, the rest remains in effect and the provision is modified to the minimum extent necessary.</li>
        <li><strong>No waiver.</strong> Failure to enforce a provision is not a waiver of it.</li>
        <li><strong>Force majeure.</strong> Neither party is liable for delay or failure caused by events beyond its reasonable control.</li>
        <li><strong>Notices.</strong> We may give notice by email or in-product. Legal notices to us must be sent to <a href="mailto:legal@getrepcheck.com" className="text-brand-600 underline underline-offset-2 hover:text-brand-700">legal@getrepcheck.com</a> and are effective on receipt.</li>
        <li><strong>Relationship.</strong> The parties are independent contractors; nothing creates a partnership, agency or employment relationship.</li>
      </UL>

      <H2 id="contact">20. Contact</H2>
      <P>
        Questions about these Terms: <a href="mailto:legal@getrepcheck.com" className="text-brand-600 underline underline-offset-2 hover:text-brand-700">legal@getrepcheck.com</a>. We aim to respond within five business days.
      </P>
    </LegalLayout>
  );
}
